import React, { useState, useCallback } from "react";
import {
  Box, Paper, Typography, TextField, Button, Stack, Table, TableBody, TableHead,
  TableRow, TableCell, CircularProgress, Divider, Alert,
} from "@mui/material";
import api from "../services/api";

const iso = (d) => d.toISOString().slice(0, 10);
const today = () => iso(new Date());
const firstOfThisMonth = () => { const d = new Date(); return iso(new Date(d.getFullYear(), d.getMonth(), 1)); };
const firstOfLastMonth = () => { const d = new Date(); return iso(new Date(d.getFullYear(), d.getMonth() - 1, 1)); };
const lastOfLastMonth = () => { const d = new Date(); return iso(new Date(d.getFullYear(), d.getMonth(), 0)); };

const fmt = (n) => {
  const v = Number(n) || 0;
  const s = Math.abs(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return v < 0 ? `(${s})` : s;
};
const pct = (a, b) => {
  const base = Math.abs(Number(b) || 0);
  if (base < 0.005) return "";
  const p = ((Number(a) - Number(b)) / base) * 100;
  return `${p >= 0 ? "+" : ""}${p.toFixed(1)}%`;
};

/* Merge two [{account, amount}] lists into rows keyed by account. */
function mergeAccounts(listA, listB) {
  const map = new Map();
  (listA || []).forEach((r) => { map.set(r.account, { account: r.account, a: r.amount, b: 0 }); });
  (listB || []).forEach((r) => {
    const e = map.get(r.account) || { account: r.account, a: 0, b: 0 };
    e.b = r.amount; map.set(r.account, e);
  });
  return Array.from(map.values()).sort((x, y) => (y.a + y.b) - (x.a + x.b));
}

function DataRow({ label, a, b, bold, strong, section }) {
  const delta = (Number(a) || 0) - (Number(b) || 0);
  const border = bold || strong;
  const cell = (extra = {}) => ({
    border: 0, borderTop: border ? (strong ? "2px solid" : "1px solid") : 0, borderColor: strong ? "text.primary" : "divider",
    py: border ? 0.7 : 0.4, fontWeight: strong ? 800 : bold ? 700 : 400, fontVariantNumeric: "tabular-nums", ...extra,
  });
  if (section) {
    return <TableRow><TableCell colSpan={4} sx={{ border: 0, pt: 1.5, pb: 0.3, fontWeight: 700, fontSize: 15 }}>{label}</TableCell></TableRow>;
  }
  return (
    <TableRow>
      <TableCell sx={{ ...cell(), pl: bold || strong ? 3 : 4, fontWeight: strong ? 800 : bold ? 700 : 400, fontVariantNumeric: "normal" }}>{label}</TableCell>
      <TableCell align="right" sx={cell()}>{fmt(b)}</TableCell>
      <TableCell align="right" sx={cell()}>{fmt(a)}</TableCell>
      <TableCell align="right" sx={cell({ color: delta < 0 ? "error.main" : delta > 0 ? "success.main" : "text.secondary" })}>
        {fmt(delta)}{pct(a, b) ? ` (${pct(a, b)})` : ""}
      </TableCell>
    </TableRow>
  );
}

export default function ComparativeProfitLoss() {
  const [bFrom, setBFrom] = useState(firstOfLastMonth());
  const [bTo, setBTo] = useState(lastOfLastMonth());
  const [aFrom, setAFrom] = useState(firstOfThisMonth());
  const [aTo, setATo] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const run = useCallback(() => {
    if (!bFrom || !bTo || !aFrom || !aTo) { setError("Pick both date ranges"); return; }
    setLoading(true); setError(null);
    Promise.all([
      api.get("/accounting/profit-loss", { params: { from: aFrom, to: aTo } }).then((r) => r.data),
      api.get("/accounting/profit-loss", { params: { from: bFrom, to: bTo } }).then((r) => r.data),
    ])
      .then(([a, b]) => setData({ a, b }))
      .catch((e) => setError(e.response?.data?.error || "Failed to build comparative Profit & Loss"))
      .finally(() => setLoading(false));
  }, [aFrom, aTo, bFrom, bTo]);

  const income = data ? mergeAccounts(data.a.income, data.b.income) : [];
  const expenses = data ? mergeAccounts(data.a.expenses, data.b.expenses) : [];

  return (
    <Box sx={{ width: "100%" }}>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>Comparative Profit &amp; Loss</Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        Two periods side by side with the change. Defaults to last month vs. this month.
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack spacing={2}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
            <Typography sx={{ minWidth: 110, fontWeight: 600 }}>Period A</Typography>
            <TextField type="date" size="small" label="From" InputLabelProps={{ shrink: true }} value={bFrom} onChange={(e) => setBFrom(e.target.value)} />
            <TextField type="date" size="small" label="To" InputLabelProps={{ shrink: true }} value={bTo} onChange={(e) => setBTo(e.target.value)} />
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
            <Typography sx={{ minWidth: 110, fontWeight: 600 }}>Period B</Typography>
            <TextField type="date" size="small" label="From" InputLabelProps={{ shrink: true }} value={aFrom} onChange={(e) => setAFrom(e.target.value)} />
            <TextField type="date" size="small" label="To" InputLabelProps={{ shrink: true }} value={aTo} onChange={(e) => setATo(e.target.value)} />
            <Button variant="contained" onClick={run} disabled={loading}>
              {loading ? <CircularProgress size={20} /> : "Run report"}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {data && !loading && (
        <Paper sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ textAlign: "center", mb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Comparative Profit &amp; Loss</Typography>
          </Box>
          <Divider sx={{ mb: 1 }} />

          <Box sx={{ overflowX: "auto" }}>
            <Table size="small" sx={{ maxWidth: 820, mx: "auto" }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Account</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>A: {data.b.fromDate}→{data.b.toDate}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>B: {data.a.fromDate}→{data.a.toDate}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Change (B−A)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <DataRow label="Income" section />
                {income.map((r) => <DataRow key={`i-${r.account}`} label={r.account} a={r.a} b={r.b} />)}
                <DataRow label="Total Income" a={data.a.incomeTotal} b={data.b.incomeTotal} bold />

                <DataRow label="Cost of Goods Sold" section />
                <DataRow label="Total Cost of Goods Sold" a={data.a.cogsTotal} b={data.b.cogsTotal} bold />

                <DataRow label="Gross Profit" a={data.a.grossProfit} b={data.b.grossProfit} strong />

                <DataRow label="Expenses" section />
                {expenses.map((r) => <DataRow key={`e-${r.account}`} label={r.account} a={r.a} b={r.b} />)}
                <DataRow label="Total Expenses" a={data.a.expenseTotal} b={data.b.expenseTotal} bold />

                <DataRow label="Net Income" a={data.a.netIncome} b={data.b.netIncome} strong />
              </TableBody>
            </Table>
          </Box>

          <Typography variant="caption" sx={{ display: "block", mt: 2, color: "text.secondary" }}>
            Same figures as the Profit &amp; Loss report, run for each period. COGS is the periodic figure
            (Beginning + Purchases − Ending Inventory). Green = better for profit, red = worse.
          </Typography>
        </Paper>
      )}
    </Box>
  );
}
